import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import DateInput from './DateInput.jsx';

// Spec rule: status toggle is on/off. The API uses a string for sub_status;
// the source app treats truthy / case-insensitive "true"/"on"/"1" as enabled.
function isOn(v) {
  if (typeof v === 'boolean') return v;
  if (v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s === 'true' || s === 'on' || s === '1' || s === 'enabled' || s === 'active' || s === 'yes';
}

function moneyFmt(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function BackOfficeSubscriptionDialog({ clientId, siteId, siteName, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({}); // keyed by plan_id
  const [loading, setLoading] = useState(false);
  const [postingPlanId, setPostingPlanId] = useState(null);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`/api/backoffice/sites/${clientId}/${siteId}/subscriptions`);
      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data);
      const next = {};
      for (const r of data) {
        next[r.plan_id] = {
          sub_id: r.id || r.sub_id || null,
          sub_status: r.subscription_status,
          due: r.due_date ? String(r.due_date).slice(0, 10) : '',
          payment_method: r.payment_method || '',
          monthly_amount: r.monthly_amount != null ? String(r.monthly_amount) : '',
          yearly_amount:  r.yearly_amount  != null ? String(r.yearly_amount)  : '',
          on: isOn(r.subscription_status),
        };
      }
      setDrafts(next);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, siteId]);

  useEffect(() => { load(); }, [load]);

  function setDraft(planId, patch) {
    setDrafts(prev => ({ ...prev, [planId]: { ...(prev[planId] || {}), ...patch } }));
  }

  async function post(planId) {
    const d = drafts[planId];
    if (!d) return;
    setPostingPlanId(planId); setError(null);
    try {
      const body = {
        sub_id: d.sub_id ?? null,
        sub_status: d.on ? 'true' : 'false',
        due: d.due || null,
        payment_method: d.payment_method || '',
        monthly_amount: d.monthly_amount === '' ? 0 : Number(d.monthly_amount),
        yearly_amount:  d.yearly_amount  === '' ? '' : Number(d.yearly_amount), // server fills the 0.9 default if blank
      };
      await axios.post(`/api/backoffice/subscriptions/${clientId}/${siteId}/${planId}`, body);
      setSavedAt(planId);
      setTimeout(() => setSavedAt(null), 1800);
      if (onSaved) onSaved();
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setPostingPlanId(null);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>Billing & Subscription — {siteName || `Site ${siteId}`}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {loading && <p>Loading subscriptions…</p>}
        {error && <p className="error">{error}</p>}

        {!loading && (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Next Charge</th>
                  <th>Payment</th>
                  <th>Monthly</th>
                  <th>Yearly</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const d = drafts[r.plan_id] || {};
                  const isPosting = postingPlanId === r.plan_id;
                  return (
                    <tr key={r.plan_id}>
                      <td>{r.name}</td>
                      <td>
                        <label className="toggle">
                          <input
                            type="checkbox"
                            checked={!!d.on}
                            onChange={(e) => setDraft(r.plan_id, { on: e.target.checked })}
                          />
                          <span>{d.on ? 'On' : 'Off'}</span>
                        </label>
                      </td>
                      <td>
                        <DateInput
                          value={d.due || ''}
                          onChange={(e) => setDraft(r.plan_id, { due: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="form-input"
                          value={d.payment_method || ''}
                          onChange={(e) => setDraft(r.plan_id, { payment_method: e.target.value })}
                          placeholder="e.g. credit, ach"
                        />
                      </td>
                      <td>
                        <input
                          className="form-input cell-mono"
                          type="number" step="0.01"
                          value={d.monthly_amount}
                          onChange={(e) => setDraft(r.plan_id, { monthly_amount: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="form-input cell-mono"
                          type="number" step="0.01"
                          value={d.yearly_amount}
                          onChange={(e) => setDraft(r.plan_id, { yearly_amount: e.target.value })}
                          placeholder={d.monthly_amount ? moneyFmt(Number(d.monthly_amount) * 12 * 0.9) : ''}
                        />
                      </td>
                      <td>
                        <button
                          className="btn-primary"
                          disabled={isPosting}
                          onClick={() => post(r.plan_id)}
                        >
                          {isPosting ? 'Posting…' : (savedAt === r.plan_id ? 'Saved' : 'Post')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
